import { Controller, Get, NotFoundException } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { StoreConfigResponse, StoreInfoResponse } from '@cart/shared-types';
import { StoreContextService } from './store-context.service';
import { StoreConfigService } from './store-config.service';
import { StoreSitemapService, SitemapEntry } from './store-sitemap.service';

// Public, no guard — matches /store/theme's pattern: the storefront calls
// this once at boot, before any auth state exists, to learn whether the
// current subdomain resolved to a real, active store.
@Controller('store')
export class StoreInfoController {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext: StoreContextService,
    private readonly storeConfig: StoreConfigService,
    private readonly sitemap: StoreSitemapService
  ) {}

  /**
   * Sitemap *data* for the current tenant — paths, not absolute URLs.
   *
   * `web/src/server.ts` serves the real `/sitemap.xml` by calling this and
   * prefixing the public origin. The API sits behind a reverse proxy and its
   * own `Host` is the internal upstream address, so building absolute URLs here
   * would emit a sitemap full of addresses no crawler can reach.
   */
  @Get('sitemap')
  async getSitemap(): Promise<{ entries: SitemapEntry[] }> {
    if (!this.storeContext.resolved()) {
      throw new NotFoundException('Store not found');
    }
    return { entries: await this.sitemap.entries() };
  }

  @Get('info')
  async getInfo(): Promise<StoreInfoResponse> {
    if (!this.storeContext.resolved()) {
      throw new NotFoundException('Store not found');
    }

    const store = await this.prisma.store.findUnique({
      where: { id: this.storeContext.storeId() },
      select: { id: true, name: true, defaultCurrency: true },
    });

    if (!store) {
      throw new NotFoundException('Store not found');
    }

    return { id: store.id, name: store.name, currency: store.defaultCurrency };
  }

  /**
   * The storefront's single boot round trip: tenant identity + theme
   * (presentation) + storefront template (structure), plus the entitlement
   * flag the admin banner reads.
   *
   * Unguarded and tenant-scoped via StoreContextService for the same reason
   * `/store/info` and `/store/theme` are — it runs during SSR, before any auth
   * state exists. Those two endpoints are unchanged and still served: this one
   * is additive, and the storefront keeps working if it is never called.
   *
   * Resolution and caching live in StoreConfigService; this handler only maps
   * an unresolved host to a 404.
   */
  @Get('config')
  getConfig(): Promise<StoreConfigResponse> {
    if (!this.storeContext.resolved()) {
      throw new NotFoundException('Store not found');
    }

    return this.storeConfig.getConfig(this.storeContext.storeId());
  }
}
